import { html, customElement } from '@adobe/edex/ui/edex-lit';

import { $t } from '@adobe/edex/ui/shared/config/i18n';
import { I18nKey } from '@adobe/edex/ui/shared/constants/i18n';

import '@adobe/edex/ui/components/shared/editor/editor';
import { createComment, setCommentForm, updateComment, updateCommentForm } from '@adobe/edex/ui/store/routines';
import { EdexTagName, EdexDomID } from '@adobe/edex/ui/shared/constants';
import styles from './comments-list.scss';
import { BaseConnectedElement } from '@adobe/edex/ui/components/base.element';
import { CommentFormState } from '@adobe/edex/ui/store/reducers/forms/comment';
import { selectCommentForm } from '@adobe/edex/ui/store/selectors';
import { sanitize } from '@adobe/edex/ui/utils';
import { EditorData } from '@adobe/edex/ui/types';

@customElement(EdexTagName.commentForm)
export class CommentForm extends BaseConnectedElement {
  static styles = styles;

  static get properties() {
    return {
      form: { attribute: false },
      editorData: { attribute: false },
    };
  }

  form: CommentFormState;
  editorData: EditorData;

  stateChanged(state) {
    this.form = selectCommentForm(state);
  }

  actions = {
    updateComment: updateComment.trigger,
    createComment: createComment.trigger,
    updateCommentForm: updateCommentForm.trigger,
    setCommentForm: setCommentForm.trigger,
  };

  get useLightDom(): boolean {
    return true;
  }

  handleCancel() {
    this.actions.setCommentForm(null);
  }

  handleChange({ detail: editorData }: CustomEvent<EditorData>) {
    this.editorData = editorData;
    this.actions.updateCommentForm({ comment: editorData.html });
  }

  toggleSubscribed() {
    this.actions.updateCommentForm({ subscribed: !this.form.data.subscribed });
  }

  async onSubmit() {
    const {
      form: {
        data: { id, parentCommentID, entityID, entityType, comment: dirty, subscribed },
      },
    } = this;
    const comment = sanitize(dirty);
    if (id) {
      this.actions.updateComment({
        id,
        data: { comment, subscribed },
      });
    } else {
      this.actions.createComment({
        data: {
          parentCommentID,
          entityID,
          entityType,
          comment,
          subscribed,
        },
      });
    }
  }

  render() {
    if (!this.form.data) {
      return '';
    }
    const {
      form: {
        loading,
        data: { comment, subscribed },
      },
      editorData,
    } = this;
    const value = comment || '';

    return html`
      <div class="comment-form">
        <edex-editor @change=${this.handleChange} .value=${value} placeholder="Comment"></edex-editor>
        <div class="btn-container">
          <sp-checkbox @change=${this.toggleSubscribed} ?checked="${subscribed}">
            ${$t(I18nKey.emailCommentSubscribe)}
          </sp-checkbox>
          <sp-button
            id=${EdexDomID.cancelCommentBtn}
            style="margin-right: 1rem;"
            @click="${this.handleCancel}"
            .disabled=${loading}
            variant="secondary"
          >
            ${$t(I18nKey.cancel)}
          </sp-button>
          <sp-button
            id=${EdexDomID.submitCommentBtn}
            @click="${this.onSubmit}"
            .disabled=${loading || !editorData?.plain}
          >
            ${$t(I18nKey.submit)}
          </sp-button>
        </div>
      </div>
    `;
  }
}
